/**
 * Mock library items — replace with API in production.
 *
 * The catalog uses the same large standing bank as Add questions
 * (`ASSESSMENT_BANK_QUESTIONS`, ~1.2K rows) so pagination and folder counts
 * exercise realistic scale.
 */

import {
  ASSESSMENT_BANK_FOLDERS,
  ASSESSMENT_BANK_QUESTIONS,
} from "@/lib/mock/assessment-add-questions"
import {
  assessmentBankFoldersToLibraryFolders,
  assessmentBankQuestionToLibraryItem,
} from "@/lib/mock/assessment-bank-to-library"
import { AI_SPOTTED_CASE_ITEMS } from "@/lib/mock/ai-spotted-case-questions"
import { LIBRARY_FAVORITES_FOLDER_ID } from "@/lib/library-nav"
import type { LibraryAiSpotted } from "@/lib/library-ai-spotted"
import {
  normalizeQuestionType,
  QUESTION_TYPE_IDS,
  QUESTION_TYPE_LABEL,
  type QuestionTypeId,
} from "@/lib/question-type"
import {
  type LibraryFolder,
} from "@/lib/mock/library-folders"

/** The storage enum, from the shared model. Alias kept for existing imports. */
export type LibraryItemType = QuestionTypeId

export type LibraryLevel = "easy" | "medium" | "hard"

/**
 * Labels for the Type column, filters, and search chips.
 *
 * The same spelling the pool grid and the paper roster print. This map used
 * to say "Multiple choice" and "True / false" while they said "Multiple
 * Choice" and "True or False", so one question read as two types depending
 * on which list the author was looking at.
 */
export const LIBRARY_ITEM_TYPE_LABEL: Record<LibraryItemType, string> =
  QUESTION_TYPE_LABEL

export const LIBRARY_ITEM_TYPE_ICON: Record<LibraryItemType, string> = {
  multiple_choice: "fa-list-check",
  multiple_select: "fa-list-check",
  true_false: "fa-toggle-on",
  short_answer: "fa-pen-line",
  essay: "fa-align-left",
  fill_blank: "fa-input-text",
  matching: "fa-left-right",
  hotspot: "fa-bullseye",
}

export const LIBRARY_ITEM_TYPES: LibraryItemType[] = [...QUESTION_TYPE_IDS]

/**
 * Pool / paper wording back to the catalog storage enum.
 *
 * @deprecated Call `normalizeQuestionType`. It takes every spelling the app
 * produces, including the ones this map never listed.
 */
export function libraryTypeFromPool(type: string): LibraryItemType | undefined {
  return normalizeQuestionType(type) ?? undefined
}

/** Catalog enum back to pool wording for preview and compose. */
export const POOL_TYPE_FROM_LIBRARY: Record<LibraryItemType, string> =
  QUESTION_TYPE_LABEL

/** Revised Bloom's taxonomy level (inspector / analytics). */
export type LibraryBloomLevel =
  | "Remember"
  | "Understand"
  | "Apply"
  | "Analyze"
  | "Evaluate"
  | "Create"

/** @deprecated Use `LibraryBloomLevel` — legacy tier strings still accepted on rows. */
export type LibraryTierLevel = LibraryBloomLevel

export interface LibraryItem extends Record<string, unknown> {
  id: string
  /** Stable human-facing identifier (catalog, search, citations). */
  questionId: string
  /**
   * Short name the item is filed and talked about under. The stem is what a
   * student reads; this is what a colleague calls it. Optional because rows
   * created before the field existed fall back to `questionId`.
   */
  title?: string
  /** Short preview / stem */
  stem: string
  topic: string
  status?: "Saved" | "Draft"
  type: LibraryItemType
  difficulty: LibraryLevel | ""
  author: string
  /** Work email for the primary author (demo; optional when API omits). */
  authorEmail?: string
  updatedAt: string
  /** Folder tree id (`lib/mock/library-folders.ts`). */
  folderId: string
  /** Every location this question is filed in. `folderId` is the first. */
  folderIds?: string[]
  /** Multiple choice options (only for type: "multiple_choice") */
  options?: {
    text: string
    isCorrect?: boolean
  }[]
  /** Stable bank code for lists / inspector (e.g. PH-ANA-001). */
  itemCode?: string
  bloomLevel?: LibraryBloomLevel | string
  /** Topic-style tags for inspector (# displayed in UI). */
  tags?: string[]
  /** ISO-ish created date (inspector “Creator & history”). */
  createdAt?: string
  /** Display name for “Created by” when different from `author`. */
  createdBy?: string
  /** Last editor display name (inspector copy). */
  lastEditedBy?: string
  /** Point Biserial correlation coefficient */
  ptBiserial?: number
  /** Difficulty percentage (p-value) */
  difficultyPercent?: number
  /** Number of times used in an assessment */
  usageCount?: number
  /** Precomposed “Last edited” line; overrides derived copy when set. */
  lastEditedSummary?: string
  /** Revision label e.g. v4 */
  version?: string
  examUsageCount?: number
  /** Point-biserial index (psychometrics preview). */
  pbi?: number
  /** Mean percent correct when used in assessments. */
  avgScoreCorrectPct?: number
  /** Where / when the item was last used on an exam. */
  lastUsedLabel?: string
  /** Starred outside the Favorites folder (list landing demo). */
  isStarred?: boolean
  /** When an import row landed in the bank (ms). Imported scope only. */
  importedAt?: number
  /** Import triage signals for the Imported questions table. */
  importSignals?: {
    duplicate: boolean
    needsAttention: boolean
  }
  /** Source document name for imported rows. */
  importSourceLabel?: string
  /** Session-new highlight for HubTable (`bg-dt-new-row-bg` + dot). */
  isNew?: boolean
  /** AI Spotted column. See `libraryAiSpotted`. */
  aiSpotted?: LibraryAiSpotted
  /** What Leo changed, shown from the Looks good info control. */
  fixedSummary?: string
  /**
   * Exam category ids chosen in the question sheet. When set, the Category
   * column reads these instead of inferring a category from `topic`.
   */
  categoryIds?: string[]
  /** True when this question has no category. Topic must not fill one in. */
  categoryUnassigned?: boolean
  categoryMappings?: {
    id: string
    categoryId: string
    categoryCode: string
    categoryName: string
    frameworkId: string
    status: "approved" | "suggested"
    confidence?: number
    source: "manual" | "auto"
  }[]
}

/** New mock rows — assign a unique `questionId` when creating client-side. */
export function newLibraryQuestionId(): string {
  return `LIB-NEW-${Date.now().toString(36).toUpperCase()}`
}

/**
 * One row per question, from one list.
 *
 * The library used to be `AI_SPOTTED_CASE_ITEMS` concatenated with the
 * pool, and the pool did not contain the catalog questions at all — so the
 * 28 CASE-2026 items existed in the library and on the assessment paper
 * but were missing from Add questions entirely. Now the pool holds every
 * question and the library is a view of it: the catalog rows render
 * through their richer library shape, everything else through the bank
 * adapter, and nothing appears twice.
 */
const CATALOG_LIBRARY_BY_ID = new Map(
  AI_SPOTTED_CASE_ITEMS.map((item) => [
    item.id.replace(/^ai-case-/, ""),
    item,
  ]),
)

/** Demo stars for the Favorites scope (folder + `isStarred` rows). */
const LIBRARY_DEMO_FAVORITE_IDS = new Set([
  "ai-case-q1",
  "ai-case-q4",
  "ai-case-q12",
  "bank-q2",
  "bank-q5",
])

export const LIBRARY_ITEMS: LibraryItem[] = ASSESSMENT_BANK_QUESTIONS.map(
  (row, index) => {
    const item =
      CATALOG_LIBRARY_BY_ID.get(row.id) ??
      assessmentBankQuestionToLibraryItem(row, index)
    if (!LIBRARY_DEMO_FAVORITE_IDS.has(row.id)) return item
    return {
      ...item,
      isStarred: true,
      folderId: LIBRARY_FAVORITES_FOLDER_ID,
    }
  },
)

/** PT folder tree plus the NURS-210 subject tree the standing bank files under. */
export const LIBRARY_FOLDERS: LibraryFolder[] = [
  {
    id: "fld-favorites",
    name: "Favorites",
    parentId: null,
    icon: "fa-star",
    colorKey: "warning",
  },
  ...assessmentBankFoldersToLibraryFolders(ASSESSMENT_BANK_FOLDERS),
]

/** @deprecated PT showcase rows — kept for catalog previews that need rich metadata. */
export const LIBRARY_SHOWCASE_ITEMS: LibraryItem[] = [
  {
    id: "q1",
    questionId: "LIB-2026-001",
    title: "Trendelenburg sign",
    stem: "During single limb stance, a drop of the contralateral pelvis indicates weakness of which muscle group?",
    topic: "Gait & Posture Analysis",
    status: "Saved" as const,
    type: "multiple_choice" as const,
    difficulty: "medium" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-28",
    folderId: "fld-favorites",
    itemCode: "LIB-2026-001",
    bloomLevel: "Understand",
    tags: ["Tag 1", "Tag 2", "Tag 3"],
    createdAt: "2024-11-02",
    createdBy: "Owner B",
    lastEditedBy: "Owner C",
    lastEditedSummary: "14 months ago · Owner C",
    version: "v4",
    examUsageCount: 22,
    pbi: 0.48,
    avgScoreCorrectPct: 88,
    lastUsedLabel: "Reference set · 14 months ago",
    options: [
      { text: "Option A", isCorrect: true },
      { text: "Option B", isCorrect: false },
      { text: "Option C", isCorrect: false },
      { text: "Option D", isCorrect: false },
    ],
    categoryMappings: [
      { id: "m-q1-1", categoryId: "ilo1_1", categoryCode: "ILO 1.1", categoryName: "Demonstrates clear written documentation", frameworkId: "ilo", status: "approved", source: "manual" },
      { id: "m-q1-2", categoryId: "plo1_1", categoryCode: "PLO 1.1", categoryName: "Formulates accurate clinical diagnoses", frameworkId: "plo", status: "approved", source: "manual" },
      { id: "m-q1-3", categoryId: "capte7_a_1", categoryCode: "CAPTE 7A.1", categoryName: "Anatomical & biomechanical principles", frameworkId: "capte_2024", status: "suggested", confidence: 0.92, source: "auto" }
    ]
  },
  {
    id: "q2",
    questionId: "LIB-2026-002",
    title: "Grade V mobilization",
    stem: "A grade V mobilization is performed within the patient's available range of motion.",
    topic: "Manual Therapy Techniques",
    status: "Draft" as const,
    type: "true_false" as const,
    difficulty: "easy" as const,
    author: "Owner B",
    authorEmail: "owner.b@demo.exxat.io",
    updatedAt: "2026-03-27",
    folderId: "fld-skills-lab",
    isStarred: true,
    pbi: 0.55,
    categoryMappings: [
      { id: "m-q2-1", categoryId: "ilo2_1", categoryCode: "ILO 2.1", categoryName: "Performs database search & literature analysis", frameworkId: "ilo", status: "suggested", confidence: 0.88, source: "auto" }
    ]
  },
  {
    id: "q3",
    questionId: "LIB-2026-003",
    title: "Facial nerve pathway",
    stem: "Name the cranial nerve that supplies the muscles of facial expression and describe its course.",
    topic: "Neuroanatomy",
    status: "Saved" as const,
    type: "short_answer" as const,
    difficulty: "hard" as const,
    author: "Owner C",
    authorEmail: "owner.c@demo.exxat.io",
    updatedAt: "2026-03-26",
    folderId: "fld-neuro-anat",
    isStarred: true,
    pbi: 0.14,
    categoryMappings: [
      { id: "m-q3-1", categoryId: "plo1_2", categoryCode: "PLO 1.2", categoryName: "Develops patient-centered interventions", frameworkId: "plo", status: "approved", source: "manual" }
    ]
  },
  {
    id: "q4",
    questionId: "LIB-2026-004",
    title: "Minimum necessary standard",
    stem: "Under HIPAA, which disclosure best reflects the minimum necessary standard?",
    topic: "HIPAA & Confidentiality",
    type: "multiple_choice" as const,
    difficulty: "medium" as const,
    author: "Owner D",
    authorEmail: "owner.d@demo.exxat.io",
    updatedAt: "2026-03-25",
    folderId: "fld-hipaa",
    isStarred: true,
    pbi: 0.19,
    options: [
      { text: "Option A", isCorrect: true },
      { text: "Option B", isCorrect: false },
      { text: "Option C", isCorrect: false },
      { text: "Option D", isCorrect: false },
    ],
    categoryMappings: [
      { id: "m-q4-1", categoryId: "capte1_2", categoryCode: "CAPTE 1B", categoryName: "Adheres to professional codes of ethics", frameworkId: "capte_2024", status: "approved", source: "manual" }
    ]
  },
  {
    id: "q5",
    questionId: "LIB-2026-005",
    title: "Antalgic gait",
    stem: "In two sentences, describe the gait deviation you would expect in a patient with an antalgic pattern.",
    topic: "Gait & Posture Analysis",
    type: "short_answer" as const,
    difficulty: "easy" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-24",
    folderId: "fld-favorites",
    categoryMappings: [
      { id: "m-q5-1", categoryId: "capte7_a_1", categoryCode: "CAPTE 7A.1", categoryName: "Anatomical & biomechanical principles", frameworkId: "capte_2024", status: "suggested", confidence: 0.95, source: "auto" }
    ]
  },
  {
    id: "q6",
    questionId: "LIB-2026-006",
    title: "Corticospinal decussation",
    stem: "At which anatomical level does the majority of the corticospinal tract cross the midline?",
    topic: "Neuroanatomy",
    type: "multiple_choice" as const,
    difficulty: "medium" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-23",
    folderId: "fld-patient-eval",
    isStarred: true,
    options: [
      { text: "Option A", isCorrect: true },
      { text: "Option B", isCorrect: false },
      { text: "Option C", isCorrect: false },
      { text: "Option D", isCorrect: false },
    ],
  },
  {
    id: "q7",
    questionId: "LIB-2026-007",
    title: "Muscle energy technique",
    stem: "Muscle energy technique requires an active contraction from the patient.",
    topic: "Manual Therapy Techniques",
    type: "true_false" as const,
    difficulty: "easy" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-22",
    folderId: "fld-msk-manual",
    isStarred: true,
  },
  {
    id: "q8",
    questionId: "LIB-2026-008",
    title: "Ventilatory threshold",
    stem: "Which physiological change marks the onset of the first ventilatory threshold during graded exercise?",
    topic: "Exercise Physiology",
    type: "multiple_choice" as const,
    difficulty: "hard" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-21",
    folderId: "fld-exercise-phys",
  },
  {
    id: "q9",
    questionId: "LIB-2026-009",
    title: "Verbal consent for records",
    stem: "A patient's verbal consent is sufficient to release their record to a third party payer.",
    topic: "HIPAA & Confidentiality",
    type: "true_false" as const,
    difficulty: "medium" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-20",
    folderId: "fld-favorites",
    isStarred: true,
    categoryMappings: [
      { id: "m-q9-1", categoryId: "capte1_2", categoryCode: "CAPTE 1B", categoryName: "Adheres to professional codes of ethics", frameworkId: "capte_2024", status: "suggested", confidence: 0.91, source: "auto" }
    ]
  },
  {
    id: "q10",
    questionId: "LIB-2026-010",
    title: "Cadence measurement",
    stem: "Explain how you would measure cadence in a clinic without instrumented gait equipment.",
    topic: "Gait & Posture Analysis",
    type: "short_answer" as const,
    difficulty: "hard" as const,
    author: "Owner A",
    authorEmail: "owner.a@demo.exxat.io",
    updatedAt: "2026-03-19",
    folderId: "fld-skills-lab",
  },
  {
    id: "q11",
    questionId: "LIB-2026-011",
    title: "Red flag screening",
    stem: "A resting heart rate above 100 beats per minute in a new patient warrants referral before exercise testing.",
    topic: "Case Studies",
    type: "true_false" as const,
    difficulty: "easy" as const,
    author: "Owner E",
    authorEmail: "owner.e@demo.exxat.io",
    updatedAt: "2026-03-18",
    folderId: "fld-case-studies",
  },
  {
    id: "q12",
    questionId: "LIB-2026-012",
    title: "Dermatome mapping",
    stem: "Describe how you would map a dermatome to localize a suspected nerve root lesion.",
    topic: "Neuroanatomy",
    type: "short_answer" as const,
    difficulty: "medium" as const,
    author: "Owner B",
    authorEmail: "owner.b@demo.exxat.io",
    updatedAt: "2026-03-17",
    folderId: "fld-neuro-anat",
  },
].map((item, i) => ({
  ...item,
  ptBiserial: Number((0.15 + (i % 5) * 0.1).toFixed(2)), // e.g. 0.15, 0.25, 0.35
  difficultyPercent: 40 + (i % 6) * 10, // e.g. 40, 50, 60
  usageCount: i % 4 === 0 ? 0 : (i % 3) + 1, // e.g. 0, 2, 3, 1...
}))
